import { useAuthStore } from '@/state/store/auth';
import { getRefreshToken } from '@/modules/auth/services/auth.service';
import { isLocalhost } from './utils/localhost-checker/locahost-checker';

/**
 * HTTP Client Module
 *
 * A robust HTTP client utility that provides standardized methods for making API requests
 * with built-in error handling, authentication token management, and automatic token refresh.
 *
 * Features:
 * - Typed request/response handling with generics
 * - Standardized methods for GET, POST, PUT, DELETE operations
 * - Automatic handling of authentication token expiration
 * - Consistent error handling with custom HttpError class
 * - URL normalization for relative and absolute paths
 * - Configurable request headers
 * - Environment-based configuration
 *
 * @example
 * // GET request
 * const users = await clients.get<User[]>('users');
 *
 * // POST request with body
 * const newUser = await clients.post<User>(
 *   'users',
 *   JSON.stringify({ name: 'John', email: 'john@example.com' })
 * );
 *
 * // PUT request with custom headers
 * const updatedUser = await clients.put<User>(
 *   `users/${userId}`,
 *   JSON.stringify({ name: 'John Updated' }),
 *   { 'X-Custom-Header': 'value' }
 * );
 *
 * // DELETE request
 * const deleteResult = await clients.delete<{ success: boolean }>(`users/${userId}`);
 *
 * // Handling errors
 * try {
 *   const data = await clients.get<Data>('some-endpoint');
 *   // Process data
 * } catch (error) {
 *   if (error instanceof HttpError) {
 *     console.error(`API Error ${error.status}: ${error.message}`);
 *   }
 * }
 *
 * @note Requires environment variables:
 * - VITE_PUBLIC_BLOCKS_API_URL: Base URL for API requests
 * - VITE_X_BLOCKS_KEY: API key for authentication
 *
 */

interface Https {
  get<T>(url: string, headers?: HeadersInit): Promise<T>;
  post<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  put<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  patch<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  delete<T>(url: string, headers?: HeadersInit, body?: BodyInit): Promise<T>;
  request<T>(url: string, options: RequestOptions, isRetry?: boolean): Promise<T>;
  createHeaders(headers: any): Headers;
  handleAuthError<T>(url: string, method: string, headers: any, body: any): Promise<T>;
}

interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: HeadersInit;
  body?: BodyInit;
}

export class HttpError extends Error {
  status: number;
  error: Record<string, unknown>;

  constructor(status: number, error: Record<string, unknown>) {
    const errorMessage = typeof error.message === 'string' ? error.message : JSON.stringify(error);

    super(errorMessage);
    this.status = status;
    this.error = error;
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '');
const projectKey = import.meta.env.VITE_X_BLOCKS_KEY ?? '';
const localHostChecker = isLocalhost();

// Shared in-flight refresh call so concurrent 401s (e.g. multiple app-load
// requests) trigger a single token refresh instead of one per request. The
// SDK client (src/lib/blocks/client.ts) refreshes through this too: refresh
// tokens rotate, so a second refresh sent with the same token would be
// rejected and log the user out.
let refreshPromise: ReturnType<typeof getRefreshToken> | null = null;

function refreshAccessToken() {
  // A cookie-only session (social login) holds no token to refresh from, and IAM answers an empty
  // refresh by ending the session, so treat it as expired without calling it.
  if (!useAuthStore.getState().accessToken) {
    return Promise.reject(
      new HttpError(401, {
        error: 'invalid_refresh_token',
        error_description: 'Session expired',
      })
    );
  }
  if (!refreshPromise) {
    refreshPromise = getRefreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function refreshAndStoreTokens() {
  const refreshTokenRes = await refreshAccessToken();
  const authStore = useAuthStore.getState();
  if (refreshTokenRes.refresh_token) {
    authStore.setTokens({
      accessToken: refreshTokenRes.access_token,
      refreshToken: refreshTokenRes.refresh_token,
    });
  } else {
    authStore.setAccessToken(refreshTokenRes.access_token);
  }
  return refreshTokenRes.access_token;
}

// Only a rejection from the refresh endpoint itself (refresh token rejected as
// invalid/expired) should force a logout. Network failures / 5xx from the
// refresh call are transient - surface the error but keep the session intact
// so the next request can try the whole revalidate flow again.
export function isInvalidRefreshTokenError(error: unknown): boolean {
  return error instanceof HttpError && (error.status === 400 || error.status === 401);
}

export const clients: Https = {
  async get<T>(url: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'GET', headers });
  },

  async post<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'POST', headers, body });
  },

  async put<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'PUT', headers, body });
  },

  async patch<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'PATCH', headers, body });
  },

  async delete<T>(url: string, headers: HeadersInit = {}, body?: BodyInit): Promise<T> {
    return this.request<T>(url, { method: 'DELETE', headers, body });
  },

  async request<T>(
    url: string,
    { method, headers = {}, body }: RequestOptions,
    isRetry = false
  ): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${BASE_URL}/${url.replace(/^\//, '')}`;

    const requestHeaders = this.createHeaders(headers);

    const config: RequestInit = {
      method,
      headers: requestHeaders,
      referrerPolicy: 'no-referrer',
    };

    if (!localHostChecker) {
      config.credentials = 'include';
    }

    if (body) {
      config.body = body;
    }

    try {
      const response = await fetch(fullUrl, config);

      if (response.ok) {
        const responseText = await response.text();
        if (!responseText.trim()) {
          return null as T;
        }

        return JSON.parse(responseText) as T;
      }

      if (response.status === 401) {
        // Parse error response first to check if it's a login error
        let err;
        try {
          err = await response.json();
        } catch {
          err = { error: response.statusText || 'Unauthorized' };
        }

        // If error has error_description, it's likely a login error, throw it directly
        if (err.error_description) {
          throw new HttpError(response.status, err);
        }

        // A session expired at the identity-provider level can't be fixed by refreshing the access
        // token - only a fresh login re-establishes it - so force that immediately instead of
        // retrying against a session that is already gone.
        if (err.error === 'session_expired') {
          useAuthStore.getState().logout();
          throw new HttpError(response.status, err);
        }

        // A 401 that persists after this request already retried once means the refresh didn't
        // actually fix anything (clock skew, a token the resource server keeps rejecting, ...) -
        // force a real re-login instead of refreshing forever.
        if (isRetry) {
          useAuthStore.getState().logout();
          throw new HttpError(response.status, err);
        }

        // Otherwise, try to refresh token
        return this.handleAuthError<T>(url, method, headers, body);
      }

      let err;
      try {
        err = await response.json();
      } catch {
        err = { error: response.statusText || 'Request failed' };
      }
      throw new HttpError(response.status, err);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new HttpError(500, { error: 'Network error' });
    }
  },

  createHeaders(headers: any): Headers {
    // Always attach Bearer when we have an access token. Cookie auth alone is
    // not enough for cross-origin hosts (e.g. data.seliseblocks.com).
    const authToken = useAuthStore.getState().accessToken;

    const baseHeaders = {
      'Content-Type': 'application/json',
      'x-blocks-key': projectKey,
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    };

    const headerEntries =
      headers instanceof Headers ? Object.fromEntries(headers.entries()) : headers;

    const newHeader = new Headers({
      ...baseHeaders,
      ...headerEntries,
    });
    return newHeader;
  },

  async handleAuthError<T>(
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    headers: any,
    body: any
  ): Promise<T> {
    const authStore = useAuthStore.getState();

    // Don't gate the refresh attempt on the store holding a refresh token:
    // in non-localhost environments the real refresh token lives in an
    // httpOnly cookie (requests go out with credentials: 'include') and the
    // store's `refreshToken` field can be empty even for a valid session.
    // Always try the refresh call and let its actual response decide.
    try {
      await refreshAndStoreTokens();
    } catch (error) {
      // Only force logout when the refresh token itself was rejected as
      // invalid/expired. A transient failure (network error, 5xx) shouldn't
      // wipe a valid session - let it surface as a failed request instead.
      if (isInvalidRefreshTokenError(error)) {
        authStore.logout();
      }
      throw error;
    }

    return this.request<T>(url, { method, headers, body }, true);
  },
};
